import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { checkProfile, login, loginExternal } from '@/controller/auth';
import setAuth from '@/redux/actions/authAction';
import { toast } from '@/shared/components/UI';
import useMessageHook from '@/shared/hooks/useMessageHook';
import { AUTH_KEYS, clearAllAuthTokens, getAuthToken, setAuthData } from '@/shared/utils/authStorage';
import { useTranslations } from '@/translations/useTranslations.js';

const Login = () => {
    const { t } = useTranslations();

    const nav = useNavigate();
    const dispatch = useDispatch();

    // Form state
    const [formData, setFormData] = useState({
        email: '',
        password: '',
    });

    // Form validation errors
    const [errors, setErrors] = useState<Record<string, string>>({});

    const [loginLoading, setLoading] = useState(false);
    const { requiredMsg, minMsg, emailMsg } = useMessageHook();
    const [isRem, setRem] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const [searchParams] = useSearchParams();

    useEffect(() => {
        const source = searchParams.get('source');
        if (source !== null) {
            const data = {
                email: searchParams.get('email'),
                phoneNo: searchParams.get('phoneNo'),
                token: searchParams.get('token'),
                tenant: searchParams.get('token'),
            };
            checkExternalLogin(data);

            //check for existing email. if not available, then register as new user
        }

        let a = getAuthToken(AUTH_KEYS.ACCESS_TOKEN);
        if (a === null) {
            //do nth
        } else {
            checkAdmin();
        }

        const usn = localStorage.getItem('usn');
        if (usn !== null) {
            setFormData((prev) => ({
                ...prev,
                email: usn,
            }));
            setRem(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const checkExternalLogin = async (data: any) => {
        return await loginExternal(data).then((response: any) => {
            const item = response.data;
            setAuthData({
                accessToken: item.data.tenant.access_token,
                refreshToken: item.data.tenant.refresh_token,
                customerId: item.data.tenant.id,
                uuid: item.data.user.uuid,
                usn: item.data.user.email,
                brands: item.data.brands || [],
                isOnboarded: item.data.is_onboarded || false,
            });
            dispatch(setAuth(true));
            nav('/member/register-product');
        });
    };

    const checkAdmin = async () => {
        // start
        try {
            const ret = await checkProfile();
            if (ret.status === 200) {
                dispatch(setAuth(true));
                nav('/member/register-product');
            } else {
                clearAllAuthTokens();
            }
        } catch (e) {
            console.log(e, 'MIDDLEWARE ERROR');
            alert('System Error');
        }
        // end
    };

    // Handle form field changes
    const handleFieldChange = (field: string, value: string) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));

        if (errors[field]) {
            setErrors((prev) => {
                const updated = { ...prev };
                delete updated[field];
                return updated;
            });
        }
    };

    // Form validation
    const validateForm = () => {
        const newErrors: Record<string, string> = {};

        if (!formData.email.trim()) {
            newErrors.email = requiredMsg('email');
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = emailMsg('email');
        } else if (formData.email.length < 6) {
            newErrors.email = minMsg('email', '6');
        }

        if (!formData.password.trim()) {
            newErrors.password = requiredMsg('password');
        } else if (formData.password.length < 8) {
            newErrors.password = minMsg('password', '8');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        await onFinish(formData);
    };

    const onFinish = async (v: typeof formData) => {
        setLoading(true);
        try {
            const ret = await login(v.email, v.password);
            if (ret.status === 200) {
                setAuthData({
                    accessToken: ret.data.data.access_token,
                    refreshToken: ret.data.data.refresh_token || null,
                    customerId: ret.data.data.customer.id,
                    uuid: ret.data.data.user.uuid,
                    brands: ret.data.data.brands || [],
                    isOnboarded: ret.data.data.is_onboarded || false,
                });
                if (isRem) {
                    localStorage.setItem('usn', v.email);
                } else {
                    localStorage.removeItem('usn');
                }
                dispatch(setAuth(true));
                nav('/member/register-product');
            } else {
                if (ret.data.message === 'user_token_expired') {
                    nav('/token-expired?type=expired&email=' + v.email);
                } else if (ret.data.message === 'user_no_verified') {
                    nav('/verification?type=pending-activation&email=' + v.email);
                } else {
                    toast.error(ret.data?.error?.message);
                }
            }
        } catch (e) {
            console.error('Login error:', e);
            toast.error('Login failed. Please try again.');
        }
        setLoading(false);
    };

    const forgotPw = () => {
        nav('/forgot-password');
    };
    const goSignup = () => {
        nav('/registration');
    };

    return (
        <div className="px-4 sm:px-8 md:px-16 lg:px-32 xl:px-40 flex flex-1 justify-center py-5 font-sans bg-white">
            <div className="flex flex-col w-full max-w-2xl py-5 flex-1">
                {/* Header Section */}
                <div className="flex flex-wrap justify-between gap-3 p-4">
                    <p className="text-[#111218] tracking-light text-2xl sm:text-3xl lg:text-[32px] font-bold leading-tight m-0">
                        {t('label.member_sign_in')}
                    </p>
                    <p className="text-[#5f678c] text-sm font-normal leading-normal m-0">{t('label.login_desc')}</p>
                </div>

                {/* Login Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Email Field */}
                    <div className="flex max-w-full flex-wrap items-end gap-4 px-4 py-3">
                        <label className="flex flex-col flex-1">
                            <p className="text-gray-900 text-base font-medium pb-2">
                                {t('label.email')}
                                <span className="text-red-500 ml-1">*</span>
                            </p>
                            <input
                                type="email"
                                className={`w-full h-14 rounded-xl border ${errors.email ? 'border-red-500' : 'border-gray-300'} bg-white px-4 text-base outline-none focus:border-blue-500 focus-ring transition-colors`}
                                value={formData.email}
                                onChange={(e) => handleFieldChange('email', e.target.value)}
                                placeholder="Email"
                                maxLength={50}
                            />
                            {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                        </label>
                    </div>

                    {/* Password Field */}
                    <div className="flex max-w-full flex-wrap items-end gap-4 px-4 py-3">
                        <label className="flex flex-col flex-1">
                            <p className="text-gray-900 text-base font-medium pb-2">
                                {t('label.password')}
                                <span className="text-red-500 ml-1">*</span>
                            </p>
                            <div className="relative">
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    className={`w-full h-14 rounded-xl border ${errors.password ? 'border-red-500' : 'border-gray-300'} bg-white px-4 pr-12 text-base outline-none focus:border-blue-500 focus-ring transition-colors`}
                                    value={formData.password}
                                    onChange={(e) => handleFieldChange('password', e.target.value)}
                                    placeholder="Password"
                                    maxLength={50}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors duration-200"
                                >
                                    {showPassword ? (
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                                            />
                                        </svg>
                                    ) : (
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                            />
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                            />
                                        </svg>
                                    )}
                                </button>
                            </div>
                            {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
                        </label>
                    </div>

                    {/* Remember Me and Options */}
                    <div className="px-4 py-3">
                        <div className="flex items-center justify-between">
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={isRem}
                                    onChange={() => setRem(!isRem)}
                                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                                />
                                <span className="ml-2 text-sm font-medium text-gray-900">{t('label.remember_me')}</span>
                            </label>
                        </div>
                    </div>

                    {/* Login Button */}
                    <div className="p-4 flex justify-center">
                        <button
                            type="submit"
                            disabled={loginLoading}
                            className="flex min-w-[120px] max-w-[300px] cursor-pointer items-center justify-center overflow-hidden rounded-xl h-12 px-6 bg-gray-50 text-[#121317] border border-gray-300 text-sm font-bold leading-normal transition-colors duration-200 hover:bg-gray-200 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed w-full"
                        >
                            <span className="overflow-hidden">{loginLoading ? 'Loading...' : t('label.login')}</span>
                        </button>
                    </div>

                    {/* Forgot Password Link */}
                    <div className="px-4">
                        <div className="text-center">
                            <button
                                type="button"
                                onClick={forgotPw}
                                className="text-blue-600 text-sm font-medium hover:text-blue-700 transition-colors duration-200 hover:underline"
                            >
                                {t('label.forgot_your_password')}
                            </button>
                        </div>
                    </div>

                    {/* Sign Up Link */}
                    <div className="px-4 pb-4">
                        <div className="text-center">
                            <p className="text-sm text-gray-600">
                                Don't have an account?{' '}
                                <button
                                    type="button"
                                    onClick={goSignup}
                                    className="text-blue-600 font-medium hover:text-blue-700 transition-colors duration-200 hover:underline"
                                >
                                    Sign up here
                                </button>
                            </p>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default Login;
